import { defineStore } from "pinia"
import { HttpService } from "@/helpers/http-service"
import { CONSTANT } from "@/helpers/constant"
import _ from "lodash"

const httpService = new HttpService(false)

export type EmbedErrorCode = "notAllowedSalesRep" | "notAllowedCustomer" | "configuratorMissing" | "scTypeMismatch" | "noActiveBundle" | "buildFailedGeneric" | "unknown"

export interface EmbedError {
  code: EmbedErrorCode
  detail: string
  status: number | null
}

export interface EmbedSalesChannel {
  id: string
  name: string
  active: boolean
  hasDomain: boolean
}

export type BundleStatus = "queued" | "building" | "ready" | "failed"

export interface BundleInfo {
  id: string
  configuratorId: string
  version: string | null
  status: BundleStatus
  errorMessage: string | null
  isActive: boolean
}

export interface BundleListItem {
  id: string
  version: string | null
  sha: string | null
  status: BundleStatus
  isActive: boolean
  errorMessage: string | null
  jsPath: string | null
  cssPath: string | null
  createdAt: string | null
  updatedAt: string | null
}

export interface ManifestInfo {
  js: string | string[] | null
  css: string | string[] | null
  runtime: string | null
  version?: string | null
  [key: string]: any
}

export interface SnippetData {
  configuratorId: string
  manifestUrl: string
  loaderUrl: string
}

// Embed "chrome" = the surrounding UI elements rendered around the embedded
// configurator. Keys must stay in sync with the backend allow-list
// (EmbedChromeConfig::KEYS). All keys default to visible (true).
export const CHROME_KEYS = ["brand", "stepper", "help", "account", "cart", "language"] as const
export type ChromeKey = (typeof CHROME_KEYS)[number]
export type ChromeConfig = Record<ChromeKey, boolean>

function defaultChrome(): ChromeConfig {
  return { brand: true, stepper: true, help: true, account: true, cart: true, language: true }
}

// Resolve a raw/partial chrome payload into a full config: every known key is
// present, unknown keys are dropped, missing keys default to visible, and any
// value other than boolean true is coerced to false (mirrors the backend cast).
function resolveChrome(raw: any): ChromeConfig {
  const out = defaultChrome()
  if (raw && typeof raw === "object") {
    for (const key of CHROME_KEYS) {
      if (key in raw) out[key] = raw[key] === true
    }
  }
  return out
}

interface JcEmbedState {
  salesChannels: EmbedSalesChannel[]
  salesChannelsLoaded: boolean
  loadingSalesChannels: boolean
  currentBundle: BundleInfo | null
  bundles: BundleListItem[]
  bundlesTotal: number
  loadingBundles: boolean
  manifest: ManifestInfo | null
  snippetData: SnippetData | null
  chrome: ChromeConfig
  isPolling: boolean
  pollCancelled: boolean
  lastError: EmbedError | null
}

const POLL_INTERVAL_MS = 3000
const POLL_MAX_DURATION_MS = 5 * 60 * 1000

function mapError(error: any): EmbedError {
  const status: number | null = _.get(error, "response.status", null)
  const errorObj = _.get(error, "response.data.errors[0]", undefined)
  const detail: string = _.get(errorObj, "detail", "") || _.get(errorObj, "title", "") || _.get(error, "message", "") || ""
  const code: string = _.get(errorObj, "code", "") || ""

  const detailLower = detail.toLowerCase()

  if (status === 404 && (code === "no_active_bundle" || detailLower.includes("no_active_bundle") || detailLower.includes("no active bundle"))) {
    return { code: "noActiveBundle", detail, status }
  }
  if (status === 405) {
    if (detailLower.includes("not allowed to access this customer")) {
      return { code: "notAllowedCustomer", detail, status }
    }
    if (detailLower.includes("not allowed to access this route")) {
      return { code: "notAllowedSalesRep", detail, status }
    }
    if (detailLower.includes("configurator not found") || detailLower.includes("no owner customer")) {
      return { code: "configuratorMissing", detail, status }
    }
    if (detailLower.includes("sales channel does not belong to the embed type")) {
      return { code: "scTypeMismatch", detail, status }
    }
  }
  return { code: "unknown", detail, status }
}

export const useJcEmbedStore = defineStore({
  id: "jc.embed",
  state: (): JcEmbedState => ({
    salesChannels: [],
    salesChannelsLoaded: false,
    loadingSalesChannels: false,
    currentBundle: null,
    bundles: [],
    bundlesTotal: 0,
    loadingBundles: false,
    manifest: null,
    snippetData: null,
    chrome: defaultChrome(),
    isPolling: false,
    pollCancelled: false,
    lastError: null,
  }),
  actions: {
    async fetchSalesChannels(force = false): Promise<EmbedSalesChannel[]> {
      if (this.salesChannelsLoaded && !force) return this.salesChannels
      this.loadingSalesChannels = true
      try {
        const result: any = await httpService.get(CONSTANT.API.JERSEY.EMBED.SALES_CHANNELS)
        const list: EmbedSalesChannel[] = _.get(result, "data.salesChannels", []) || []
        list.sort((a, b) => {
          if (a.active === b.active) return (a.name || "").localeCompare(b.name || "")
          return a.active ? -1 : 1
        })
        this.salesChannels = list
        this.salesChannelsLoaded = true
        this.lastError = null
        return list
      } catch (error) {
        this.lastError = mapError(error)
        return []
      } finally {
        this.loadingSalesChannels = false
      }
    },

    async triggerBuild(configuratorId: string, withPreload = true): Promise<string | null> {
      try {
        const url = CONSTANT.API.JERSEY.EMBED.BUILD.replace("{configuratorId}", configuratorId)
        const result: any = await httpService.post(url, { withPreload })
        const bundleId: string = _.get(result, "data.bundleId", "")
        const status: BundleStatus = _.get(result, "data.status", "queued")
        if (!bundleId) {
          this.lastError = { code: "unknown", detail: "Build response missing bundleId", status: result?.status ?? null }
          return null
        }
        this.currentBundle = {
          id: bundleId,
          configuratorId,
          version: null,
          status,
          errorMessage: null,
          isActive: false,
        }
        // Refresh the history so the new (or re-used) bundle entry appears in
        // the table immediately and the per-row status updates from fetchBundleStatus
        // have a row to land in.
        await this.fetchBundles(configuratorId, { limit: 10 })
        this.lastError = null
        return bundleId
      } catch (error) {
        this.lastError = mapError(error)
        return null
      }
    },

    async fetchBundleStatus(bundleId: string): Promise<BundleInfo | null> {
      try {
        const url = CONSTANT.API.JERSEY.EMBED.BUILD_STATUS.replace("{bundleId}", bundleId)
        const result: any = await httpService.get(url)
        const data = _.get(result, "data", null)
        if (!data) return null
        const bundle: BundleInfo = {
          id: data.id,
          configuratorId: data.configuratorId,
          version: data.version ?? null,
          status: data.status,
          errorMessage: data.errorMessage ?? null,
          isActive: !!data.isActive,
        }
        this.currentBundle = bundle
        // Mirror the latest status into the local history list so the table
        // reflects status flips live (handles backend updating an existing
        // bundle row instead of inserting a new one).
        const idx = this.bundles.findIndex((b) => b.id === bundle.id)
        if (idx >= 0) {
          this.bundles[idx] = {
            ...this.bundles[idx],
            status: bundle.status,
            version: bundle.version ?? this.bundles[idx].version,
            errorMessage: bundle.errorMessage,
            isActive: bundle.isActive,
          }
        }
        return bundle
      } catch (error) {
        this.lastError = mapError(error)
        return null
      }
    },

    cancelPolling() {
      this.pollCancelled = true
      this.isPolling = false
    },

    async pollBuildStatus(bundleId: string): Promise<BundleInfo | null> {
      this.pollCancelled = false
      this.isPolling = true
      const startedAt = Date.now()
      try {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (this.pollCancelled) return this.currentBundle
          if (Date.now() - startedAt > POLL_MAX_DURATION_MS) {
            this.lastError = { code: "buildFailedGeneric", detail: "Polling-Timeout", status: null }
            return this.currentBundle
          }
          const bundle = await this.fetchBundleStatus(bundleId)
          if (!bundle) {
            return null
          }
          if (bundle.status === "ready" || bundle.status === "failed") {
            return bundle
          }
          await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS))
        }
      } finally {
        this.isPolling = false
      }
    },

    async fetchSnippetData(configuratorId: string): Promise<SnippetData | null> {
      try {
        const url = CONSTANT.API.JERSEY.EMBED.SNIPPET_DATA.replace("{configuratorId}", configuratorId)
        const result: any = await httpService.get(url)
        const data = _.get(result, "data", null)
        if (!data) {
          this.snippetData = null
          return null
        }
        this.snippetData = {
          configuratorId: data.configuratorId ?? "",
          manifestUrl: data.manifestUrl ?? "",
          loaderUrl: data.loaderUrl ?? "",
        }
        // snippet-data is the only read source for the resolved chrome config;
        // it is always fully resolved (all 6 keys) when a bundle is present.
        this.chrome = resolveChrome(data.chrome)
        this.lastError = null
        return this.snippetData
      } catch (error) {
        const mapped = mapError(error)
        this.lastError = mapped
        if (mapped.code === "noActiveBundle") {
          this.snippetData = null
        }
        return null
      }
    },

    async fetchBundles(configuratorId: string, options: { status?: BundleStatus[]; limit?: number } = {}): Promise<BundleListItem[]> {
      this.loadingBundles = true
      try {
        const url = CONSTANT.API.JERSEY.EMBED.BUNDLES.replace("{configuratorId}", configuratorId)
        const params: Record<string, string | number> = {}
        if (options.status?.length) params.status = options.status.join(",")
        if (options.limit) params.limit = options.limit
        const result: any = await httpService.get(url, Object.keys(params).length ? params : undefined)
        const list: BundleListItem[] = _.get(result, "data.bundles", []) || []
        this.bundles = list
        this.bundlesTotal = _.get(result, "data.total", list.length) || 0
        const active = list.find((b) => b.isActive) ?? list.find((b) => b.status === "ready") ?? null
        if (active) {
          this.currentBundle = {
            id: active.id,
            configuratorId,
            version: active.version,
            status: active.status,
            errorMessage: active.errorMessage,
            isActive: active.isActive,
          }
        }
        this.lastError = null
        return list
      } catch (error) {
        this.lastError = mapError(error)
        return []
      } finally {
        this.loadingBundles = false
      }
    },

    async fetchManifest(configuratorId: string): Promise<ManifestInfo | null> {
      try {
        const url = CONSTANT.API.JERSEY.EMBED.MANIFEST.replace("{configuratorId}", configuratorId)
        const result: any = await httpService.get(url)
        this.manifest = _.get(result, "data", null)
        this.lastError = null
        return this.manifest
      } catch (error) {
        const mapped = mapError(error)
        this.lastError = mapped
        if (mapped.code === "noActiveBundle") {
          this.manifest = null
        }
        return null
      }
    },

    async patchSalesChannel(configuratorId: string, salesChannelId: string): Promise<boolean> {
      try {
        const url = CONSTANT.API.JERSEY.EMBED.PATCH_CONFIGURATOR.replace("{configuratorId}", configuratorId)
        await httpService.patch(url, { salesChannelId })
        this.lastError = null
        return true
      } catch (error) {
        this.lastError = mapError(error)
        return false
      }
    },

    // Persist a partial chrome change (one or more keys). The backend accepts
    // partial payloads and only changed keys need to be sent. On success the
    // local state is merged so the UI stays consistent even without an active
    // bundle (where re-reading via snippet-data would not be possible).
    async patchChrome(configuratorId: string, partial: Partial<ChromeConfig>): Promise<boolean> {
      try {
        const url = CONSTANT.API.JERSEY.EMBED.PATCH_CONFIGURATOR.replace("{configuratorId}", configuratorId)
        await httpService.patch(url, { chrome: partial })
        this.chrome = resolveChrome({ ...this.chrome, ...partial })
        this.lastError = null
        return true
      } catch (error) {
        this.lastError = mapError(error)
        return false
      }
    },

    resetBundleState() {
      this.currentBundle = null
      this.bundles = []
      this.bundlesTotal = 0
      this.manifest = null
      this.snippetData = null
      this.chrome = defaultChrome()
      this.pollCancelled = false
      this.isPolling = false
    },
  },
})
